import { MatchCheckBox } from "@/components/jobs/match-check-box";
import { PageLayout } from "@/components/page-layout";
import { useAuthModal } from "@/context/auth-modal";
import { authClient } from "@/lib/auth-client";
import {
  formatComp,
  formatExperience,
  formatRelativeTime,
  getCountryFlag,
  getCountryName,
} from "@/utils/format";
import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@super-resume-new/ui/components/dialog";
import { Spinner } from "@super-resume-new/ui/components/spinner";
import { Textarea } from "@super-resume-new/ui/components/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  AlertOctagon,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Briefcase,
  Building2,
  CheckCircle2,
  Clock,
  DollarSign,
  ExternalLink,
  Flag,
  Layers,
  MapPin,
  PlusCircle,
  ShieldAlert,
  Wand2,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { jsonLdScript, ogImageUrl, seo } from "@/utils/seo";
import { breadcrumbSchema } from "@/utils/schema";
import { jobPostingSchema, jobWebPageSchema } from "@/utils/job-schema";

const JOB_NOT_FOUND_HEAD = seo({
  title: "Job no longer available | JobCues",
  description: "This opening has been filled, expired, or removed. Browse current verified tech jobs.",
  noindex: true,
});

export const Route = createFileRoute("/jobs/$slug")({
  loader: async ({ params }) => {
    try {
      return await client.jobs.getBySlug({ slug: params.slug });
    } catch {
      // notFound(), not a null render: a job board accumulates dead slugs, and
      // answering every one of them with a 200 teaches the crawler that this
      // whole path segment returns soft 404s.
      throw notFound();
    }
  },
  // A posting's content is fixed once ingestion has parsed it, so the rendered
  // document is the same for every visitor: the session is read client-side by
  // authClient.useSession(), never in the loader, so nothing user-specific is
  // in this HTML. Two days at the edge, and the browser still revalidates.
  headers: () => ({
    "cache-control": "public, s-maxage=172800, max-age=0, must-revalidate",
  }),
  notFoundComponent: JobNotFound,
  head: ({ loaderData }) => {
    const job = loaderData?.job;
    const intel = loaderData?.intel ?? null;

    if (!job) return JOB_NOT_FOUND_HEAD;

    const company = job.company || "a verified employer";
    const where = job.workMode === "remote" ? "Remote" : job.locationPrimary || job.country || "";
    const title = `${job.title} at ${company}${where ? ` (${where})` : ""} | JobCues`;
    // Front-loaded, self-contained, and specific: the shape an answer engine
    // can lift whole. First 40-60 words carry the answer.
    const description = [
      `${job.title} at ${company}`,
      where ? `, ${where}` : "",
      ". ",
      intel?.summary?.[0] ??
        "See the required skills, the responsibilities, and a direct application link.",
      " Upload your resume for an instant ATS match score against this posting.",
    ].join("");

    const posting = jobPostingSchema(job, intel);

    return {
      ...seo({
        title,
        description: description.slice(0, 300),
        path: `/jobs/${job.slug}`,
        type: "article",
        image: ogImageUrl({
          title: `${job.title}${job.company ? ` at ${job.company}` : ""}`,
          description: description.slice(0, 140),
          label: "Tech Job",
        }),
        // An expired row stays reachable for anyone holding the link, but a
        // dead posting in the index is a bad result and a Google for Jobs
        // policy problem.
        noindex: job.status !== "active",
      }),
      scripts: [
        ...(posting ? [jsonLdScript(posting)] : []),
        jsonLdScript(jobWebPageSchema(job)),
        jsonLdScript(
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tech Jobs", path: "/jobs" },
            { name: `${job.title}${job.company ? ` at ${job.company}` : ""}`, path: `/jobs/${job.slug}` },
          ]),
        ),
      ],
    };
  },
  component: JobDetailComponent,
});

type ReportReason = "expired" | "filled" | "broken_link" | "inaccurate" | "spam" | "other";

const REPORT_REASONS: Array<{ value: ReportReason; label: string; description: string }> = [
  {
    value: "expired",
    label: "Position Expired",
    description: "The job listing is no longer accepting applications.",
  },
  {
    value: "filled",
    label: "Position Filled",
    description: "The company has hired someone for this role.",
  },
  {
    value: "broken_link",
    label: "Broken Apply Link",
    description: "The application URL goes to a 404 or inactive page.",
  },
  {
    value: "inaccurate",
    label: "Inaccurate Information",
    description: "Salary, requirements, or location details are incorrect.",
  },
  {
    value: "spam",
    label: "Spam or Duplicate",
    description: "This is a misleading, fake, or duplicate job posting.",
  },
  { value: "other", label: "Other Issue", description: "Other feedback or corrections." },
];

function TwitterIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function WhatsAppIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...props}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function LinkedinIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

function JobNotFound() {
  return (
    <PageLayout className="max-w-xl">
      <div className="mt-16 rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-foreground">This job is no longer available</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          The posting has been filled, expired, or removed by the employer. The directory is
          refreshed hourly from company applicant tracking systems, so there is very likely a
          current equivalent.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Link to="/jobs">
            <Button className="cursor-pointer">Browse verified tech jobs</Button>
          </Link>
          <Link to="/resume-tailor">
            <Button variant="outline" className="cursor-pointer">
              Tailor my resume
            </Button>
          </Link>
        </div>
      </div>
    </PageLayout>
  );
}

function JobDetailComponent() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const { openAuthModal } = useAuthModal();
  const { data: session } = authClient.useSession();

  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<ReportReason>("expired");
  const [reportNotes, setReportNotes] = useState("");

  // Seeded from the loader, which already fetched this row for the head tags.
  // Without it the server ships "Loading job details" as the entire body: no
  // description for Google for Jobs, and nothing at all for an AI crawler,
  // which does not run JavaScript.
  const initialData = Route.useLoaderData();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["jobs", "detail", slug],
    queryFn: async () => {
      return client.jobs.getBySlug({ slug });
    },
    initialData: initialData ?? undefined,
  });

  // Saved job status for logged in user
  const { data: savedIds = [] } = useQuery({
    queryKey: ["jobs", "savedIds"],
    queryFn: async () => client.jobs.getSavedIds(),
  });

  const isSaved = Boolean(data?.job?.id && savedIds.includes(data.job.id));

  const toggleSaveMutation = useMutation({
    mutationFn: async (jobId: string) => client.jobs.toggleSave({ jobId }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["jobs", "savedIds"] });
      toast.success(
        res.saved ? "Job saved to your bookmarks!" : "Job removed from saved bookmarks.",
      );
    },
    onError: () => {
      openAuthModal({
        title: "Save Jobs",
        description: "Sign in to save jobs to your bookmarks and review them anytime.",
      });
    },
  });

  const reportMutation = useMutation({
    mutationFn: async ({
      jobId,
      reason,
      notes,
    }: {
      jobId: string;
      reason: ReportReason;
      notes?: string;
    }) => client.jobs.report({ jobId, reason, notes }),
    onSuccess: () => {
      setReportOpen(false);
      setReportNotes("");
      toast.success("Thank you for reporting! Our team will review this posting.");
    },
    onError: (err) => {
      toast.error(err.message || "Failed to submit report. Please try again.");
    },
  });

  if (isLoading) {
    return (
      <PageLayout id="job-detail-loading" className="max-w-5xl">
        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center">
          <Spinner className="mb-4 size-10 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Loading Job Details</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Extracting requirements and fit judgements…
          </p>
        </div>
      </PageLayout>
    );
  }

  if (isError || !data?.job) {
    return (
      <PageLayout id="job-detail-error" className="max-w-5xl">
        <div className="flex min-h-[350px] flex-col items-center justify-center rounded-xl border border-border bg-card p-8 text-center">
          <h3 className="text-sm font-semibold text-foreground">Job Posting Not Found</h3>
          <p className="mt-1 max-w-sm text-xs text-muted-foreground leading-relaxed">
            This job posting may have expired or is no longer accessible.
          </p>
          <Link to="/jobs" className="mt-4">
            <Button variant="outline" size="sm">
              <ArrowLeft className="size-3.5 mr-1" />
              Back to Job Directory
            </Button>
          </Link>
        </div>
      </PageLayout>
    );
  }

  const { job, intel, similarJobs } = data;
  const compString = formatComp(job.compMin, job.compMax, job.compCurrency, job.compPeriod);
  const expString = formatExperience(job.yearsMin, job.yearsMax);
  const timeAgo = formatRelativeTime(job.postedAt);

  const shareUrl =
    typeof window !== "undefined" ? window.location.href : `https://jobcues.com/jobs/${job.slug}`;
  const shareText = `Check out this ${job.title} role at ${job.company || "Tech Company"} on JobCues:`;

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleShareWhatsApp = () => {
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSubmitReport = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    reportMutation.mutate({
      jobId: job.id,
      reason: reportReason,
      notes: reportNotes.trim() || undefined,
    });
  };

  return (
    <PageLayout id="job-detail-content" className="max-w-7xl space-y-4">
      {/* Back to directory button */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <Link to="/jobs">
          <Button size="sm" variant="ghost">
            <ArrowLeft className="size-3.5" />
            Back
          </Button>
        </Link>
        {/* Share Group */}
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">Share:</span>
          <div className="ml-auto flex items-center rounded-lg border border-border bg-muted p-0.5 shadow-sm">
            <button
              type="button"
              onClick={handleShareTwitter}
              title="Share on X / Twitter"
              aria-label="Share on X"
              className="flex size-7.5 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground transition-colors cursor-pointer"
            >
              <TwitterIcon className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={handleShareWhatsApp}
              title="Share on WhatsApp"
              aria-label="Share on WhatsApp"
              className="flex size-7.5 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-emerald-500 transition-colors cursor-pointer"
            >
              <WhatsAppIcon className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={handleShareLinkedIn}
              title="Share on LinkedIn"
              aria-label="Share on LinkedIn"
              className="flex size-7.5 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-muted-foreground transition-colors cursor-pointer"
            >
              <LinkedinIcon className="size-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Job Header Card */}
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-semibold text-muted-foreground">
          <div className="flex items-center gap-2">
            <Building2 className="size-4 text-muted-foreground" />
            <span className="text-foreground text-sm font-semibold">
              {job.company || "Confidential"}
            </span>
            <span>&bull;</span>
            <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="size-3.5" />
              {timeAgo}
            </span>
          </div>
          {/* Flag / Report Button */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setReportOpen(true)}
            title="Report expired or filled position"
            className="text-muted-foreground ml-auto"
          >
            <Flag />
            <span className="hidden md:inline">Report</span>
          </Button>
          {/* Bookmark / Save Button */}
          <Button
            type="button"
            size="sm"
            variant={isSaved ? "default" : "outline"}
            onClick={() => toggleSaveMutation.mutate(job.id)}
            disabled={toggleSaveMutation.isPending}
            className={`gap-1.5 text-xs h-8.5 px-3 cursor-pointer transition ${
              isSaved
                ? "bg-amber-600 hover:bg-amber-700 text-white font-semibold border-amber-600 shadow-sm"
                : "text-foreground"
            }`}
          >
            <Bookmark className={isSaved ? "fill-white" : ""} />
            <span className="hidden md:inline">{isSaved ? "Saved" : "Save"}</span>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            {job.title}
          </h1>
        </div>

        {/* Badges / Metadata Row */}
        <div className="flex flex-wrap items-center gap-2">
          {job.workMode && (
            <Badge
              variant="secondary"
              className={`text-xs capitalize font-medium ${
                job.workMode === "remote"
                  ? "bg-brand/10 text-brand-foreground border-brand/20"
                  : job.workMode === "hybrid"
                    ? "bg-muted/10 text-muted-foreground border-border"
                    : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
              }`}
            >
              {job.workMode}
            </Badge>
          )}

          {job.country && (
            <Badge
              variant="outline"
              className="font-medium gap-1 text-xs"
              title={getCountryName(job.country)}
            >
              <span className="text-sm leading-none">{getCountryFlag(job.country)}</span>
              <span>{job.country}</span>
            </Badge>
          )}

          {job.locationPrimary && (
            <Badge variant="outline" className="text-xs">
              <MapPin className="size-3 text-muted-foreground" />
              {job.locationPrimary}
            </Badge>
          )}

          {job.seniority && (
            <Badge variant="outline" className="capitalize text-xs">
              <Briefcase className="size-3 text-muted-foreground" />
              {job.seniority}
            </Badge>
          )}

          {expString && (
            <Badge variant="outline" className="text-xs">
              {expString}
            </Badge>
          )}

          {compString && (
            <Badge
              variant="secondary"
              className="text-xs font-medium bg-emerald-500/10 text-emerald-500 border-emerald-500/20 font-mono"
            >
              <DollarSign className="size-3" />
              {compString}
            </Badge>
          )}

          {/* Action Buttons: Tailor, Boost, Outbound Apply */}
          {session?.user && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Link to="/resume-tailor" search={{ job: job.slug }}>
                <Button
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-white font-semibold gap-1.5 shadow-sm cursor-pointer"
                >
                  <Wand2 className="size-3.5" />
                  Tailor Resume
                </Button>
              </Link>

              <Link to="/ats-booster" search={{ job: job.slug }}>
                <Button
                  size="sm"
                  variant="outline"
                  className="font-semibold gap-1.5 shadow-sm cursor-pointer"
                >
                  <Zap className="size-3.5 text-emerald-500" />
                  Boost ATS
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Flag / Report Dialog Modal */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>Report Job Posting</DialogTitle>
            <DialogDescription>Select what is wrong with this listing:</DialogDescription>
          </DialogHeader>
          <DialogPanel>
            <form id="report-job-form" onSubmit={handleSubmitReport}>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  {REPORT_REASONS.map((r) => (
                    <label
                      key={r.value}
                      onClick={() => setReportReason(r.value)}
                      className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${
                        reportReason === r.value
                          ? "border-brand bg-brand/10 text-foreground"
                          : "border-border bg-card hover:bg-muted/40 text-muted-foreground"
                      }`}
                    >
                      <input
                        type="radio"
                        name="reportReason"
                        value={r.value}
                        checked={reportReason === r.value}
                        onChange={() => setReportReason(r.value)}
                        className="mt-1 accent-brand"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground">{r.label}</p>
                        <p className="text-sm text-muted-foreground">{r.description}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex flex-col gap-1.5 mt-1">
                  <label htmlFor="reportNotes" className="text-xs font-semibold text-foreground">
                    Additional details
                  </label>
                  <Textarea
                    id="reportNotes"
                    value={reportNotes}
                    onChange={(e) => setReportNotes(e.target.value)}
                    placeholder="e.g. Application portal states 'Position Closed' as of today."
                    rows={2}
                    className="text-xs"
                  />
                </div>
              </div>
            </form>
          </DialogPanel>

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setReportOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="report-job-form"
              size="sm"
              onClick={() => handleSubmitReport()}
              disabled={reportMutation.isPending}
              className="bg-primary hover:bg-primary/90 text-white font-semibold cursor-pointer"
            >
              {reportMutation.isPending ? "Submitting…" : "Submit Report"}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      {/* Recruiter Fit Breakdown (The USP Content) */}
      {intel?.summary && intel.summary.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-sm font-semibold text-foreground">
              Recruiter Fit Breakdown & Candid Summary
            </h2>
          </div>
          <div className="flex flex-col gap-2">
            {intel.summary.map((line, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2.5 text-muted-foreground leading-relaxed"
              >
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-xs font-semibold text-brand-foreground mt-0.5">
                  {idx + 1}
                </span>
                <p className="text-foreground/90">{line}</p>
              </div>
            ))}
          </div>

          {/* Seniority Mismatch Note if flagged */}
          {intel.seniorityMismatch && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
              <ShieldAlert className="size-4 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Seniority Level Notice: </span>
                <span>{intel.seniorityMismatch}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Key Responsibilities */}
      {intel?.responsibilities && intel.responsibilities.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-foreground mb-4">Role Responsibilities</h2>
          <ul className="flex flex-col gap-2">
            {intel.responsibilities.map((resp, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-base text-muted-foreground leading-relaxed"
              >
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-xs font-semibold text-brand-foreground mt-0.5">
                  {idx + 1}
                </span>
                <span className="text-foreground/90">{resp}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Skills Matrix (Must-Have vs Nice-to-Have) */}
      {intel && (intel.mustHave.length > 0 || intel.niceToHave.length > 0) && (
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex size-7 items-center justify-center rounded-lg bg-brand/10 text-brand-foreground">
                <Layers className="size-4" />
              </div>
              <h2 className="text-sm font-semibold text-foreground">Skills Matrix</h2>
            </div>
            {/* <span className="text-xs font-medium text-muted-foreground">
              {intel.mustHave.length + intel.niceToHave.length} extracted competencies
            </span> */}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Must Have Skills Panel */}
            <div className="flex flex-col gap-3 rounded-xl border border-brand/20 bg-brand/[0.03] p-4.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-muted-foreground shrink-0" />
                  <h3 className="text-sm font-semibold text-foreground">Must-Have Skills</h3>
                </div>
                <Badge
                  variant="secondary"
                  className="bg-brand/10 text-brand-foreground border-brand/20 font-mono text-[11px] px-2 py-0.5"
                >
                  {intel.mustHave.length} required
                </Badge>
              </div>

              <div className="flex flex-wrap gap-2 pt-0.5">
                {intel.mustHave.map((skill, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-brand/25 bg-card/90 px-3 py-1.5 text-xs font-semibold text-foreground shadow-sm transition hover:border-brand/50 hover:bg-card"
                  >
                    <span>{skill.name}</span>
                    {skill.aliases && skill.aliases.length > 0 && (
                      <span className="text-[10px] font-normal text-muted-foreground">
                        ({skill.aliases[0]})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Nice to Have Skills Panel */}
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-4.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PlusCircle className="size-4 text-muted-foreground shrink-0" />
                  <h3 className="text-sm font-semibold text-foreground">Nice-to-Have Skills</h3>
                </div>
                <Badge
                  variant="outline"
                  className="text-muted-foreground border-border font-mono text-[11px] px-2 py-0.5"
                >
                  {intel.niceToHave.length} preferred
                </Badge>
              </div>

              <div className="flex flex-wrap gap-2 pt-0.5">
                {intel.niceToHave.length === 0 ? (
                  <span className="text-xs text-muted-foreground italic py-1">
                    No optional skills specified for this role.
                  </span>
                ) : (
                  intel.niceToHave.map((skill, idx) => (
                    <div
                      key={idx}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card/60 px-3 py-1.5 text-xs font-medium text-foreground/85 transition hover:border-border hover:bg-card"
                    >
                      <span>{skill.name}</span>
                      {skill.aliases && skill.aliases.length > 0 && (
                        <span className="text-[10px] font-normal text-muted-foreground">
                          ({skill.aliases[0]})
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* THE MATCH HOOK (Hero CTA of the Page — Free No Login Match Check) */}
      <div id="match-check-section">
        <MatchCheckBox
          slug={job.slug}
          jobTitle={job.title}
          company={job.company}
          jobDescription={[
            job.company ? `Company: ${job.company}` : "",
            job.locationPrimary ? `Location: ${job.locationPrimary}` : "",
            job.workMode ? `Work Mode: ${job.workMode}` : "",
            intel?.summary?.length ? `Summary:\n${intel.summary.join("\n")}` : "",
            intel?.responsibilities?.length
              ? `Key Responsibilities:\n${intel.responsibilities.map((r) => `- ${r}`).join("\n")}`
              : "",
            intel?.mustHave?.length
              ? `Required Skills: ${intel.mustHave.map((s) => s.name).join(", ")}`
              : "",
            intel?.niceToHave?.length
              ? `Preferred Skills: ${intel.niceToHave.map((s) => s.name).join(", ")}`
              : "",
          ]
            .filter(Boolean)
            .join("\n\n")}
        />
      </div>

      {/* Red Flags / Grounded Observations if any */}
      {intel?.redFlags && intel.redFlags.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3 text-amber-700 dark:text-amber-400">
            <AlertOctagon className="size-4" />
            <h2 className="text-base font-semibold">Grounded Observations to Note</h2>
          </div>
          <div className="flex flex-col gap-2">
            {intel.redFlags.map((flag, idx) => (
              <div key={idx} className="rounded-lg border border-border bg-background/40 p-3">
                <p className="text-sm font-semibold text-foreground">{flag.label}</p>
                <p className="text-sm text-muted-foreground italic mt-0.5">"{flag.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outbound Apply Link (Positioned below match check as per Decision #7) */}
      <a href={job.applyUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
        {/* <div className="rounded-xl px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm border border-brand/40 bg-gradient-to-br from-brand/15 via-brand/5 to-indigo-500/10 hover:from-brand hover:via-brand hover:to-indigo-700 hover:border-brand transition group"> */}
        <div className="group flex flex-col items-start justify-between gap-4 rounded-xl border border-border px-6 py-4 transition hover:border-violet-500 sm:flex-row sm:items-center bg-background">
          <div>
            {/* <h3 className="text-base font-semibold text-brand-foreground group-hover:text-white"> */}
            <h3 className="text-base font-semibold text-brand-foreground">
              Ready to submit your application?
            </h3>
            {/* <p className="text-xs text-muted-foreground group-hover:text-white"> */}
            <p className="text-xs text-muted-foreground">
              Apply directly on {job.company || "the company"}'s official job portal.
            </p>
          </div>
          <Button size="lg" variant="outline">
            Apply
            <ExternalLink className="size-4 ml-1.5" />
          </Button>
        </div>
      </a>

      {/* Similar Open Roles */}
      {similarJobs && similarJobs.length > 0 && (
        <div className="mt-12 flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-foreground">Similar Jobs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {similarJobs.map((sim) => (
              <Link to="/jobs/$slug" key={sim.id} params={{ slug: sim.slug }}>
                <div className="rounded-xl border border-border bg-card p-4 hover:border-brand/40 transition flex flex-col justify-between gap-3 group">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground group-hover:text-brand-foreground transition-colors">
                      {sim.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {sim.company || "Confidential"} •{" "}
                      {sim.locationPrimary || sim.workMode || "Remote"}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                      Posted {formatRelativeTime(sim.postedAt)}
                    </span>
                    <Button
                      size="xs"
                      variant="ghost"
                      className="text-xs font-semibold text-muted-foreground group-hover:text-brand-foreground"
                    >
                      Check Fit <ArrowRight />
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </PageLayout>
  );
}
